import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  PlayArrow as AcceptIcon,
  Cancel as RejectIcon,
  CheckCircle as ResolveIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { maintenanceAPI } from "../../utils/api";

const AssignedTasks = () => {
  const { userProfile } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState(false);
  const [actionType, setActionType] = useState(""); // 'accept', 'reject', 'resolve'
  const [actionLoading, setActionLoading] = useState(false);
  const [actionNotes, setActionNotes] = useState("");
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await maintenanceAPI.getAssignedRequests();
      setTasks(response.data);
    } catch (error) {
      console.error("Error fetching assigned tasks:", error);
      setError("Failed to load assigned tasks");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleAction = async () => {
    if (!selectedTask || !actionType) return;

    setActionLoading(true);
    try {
      let updateData = {};

      switch (actionType) {
        case "accept":
          updateData = {
            status: "in_progress",
            workerNotes: actionNotes,
          };
          break;
        case "reject":
          updateData = {
            status: "rejected",
            rejectionReason: actionNotes,
          };
          break;
        case "resolve":
          updateData = {
            status: "resolved",
            workerNotes: actionNotes,
          };
          break;
        default:
          throw new Error("Invalid action type");
      }

      await maintenanceAPI.updateStatus(selectedTask.id, updateData);

      // Refresh tasks list
      await fetchTasks();

      // Close dialog and reset form
      setActionDialog(false);
      setActionNotes("");
      setActionType("");
      setSelectedTask(null);
    } catch (error) {
      console.error(`Error ${actionType}ing task:`, error);
      setError(`Failed to ${actionType} task`);
    } finally {
      setActionLoading(false);
    }
  };

  const openActionDialog = (task, type) => {
    setSelectedTask(task);
    setActionType(type);
    setActionDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "assigned":
        return "info";
      case "in_progress":
        return "primary";
      case "resolved":
        return "success";
      case "rejected":
        return "error";
      case "closed":
        return "default";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const getFilteredTasks = () => {
    switch (currentTab) {
      case 0: // All tasks
        return tasks;
      case 1: // New assignments (assigned status)
        return tasks.filter((task) => task.status === "assigned");
      case 2: // In progress
        return tasks.filter((task) => task.status === "in_progress");
      case 3: // Completed (resolved/rejected)
        return tasks.filter((task) =>
          ["resolved", "rejected"].includes(task.status)
        );
      default:
        return tasks;
    }
  };

  const filteredTasks = getFilteredTasks();

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6" component="h2">
          Assigned Tasks
        </Typography>
        <IconButton onClick={fetchTasks} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Task Status Tabs */}
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label={`All Tasks (${tasks.length})`} />
        <Tab
          label={`New Assignments (${
            tasks.filter((task) => task.status === "assigned").length
          })`}
        />
        <Tab
          label={`In Progress (${
            tasks.filter((task) => task.status === "in_progress").length
          })`}
        />
        <Tab
          label={`Completed (${
            tasks.filter((task) =>
              ["resolved", "rejected"].includes(task.status)
            ).length
          })`}
        />
      </Tabs>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      ) : filteredTasks.length === 0 ? (
        <Card>
          <CardContent>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
            >
              {currentTab === 0
                ? "No tasks assigned to you yet."
                : currentTab === 1
                ? "No new task assignments."
                : currentTab === 2
                ? "No tasks currently in progress."
                : "No completed tasks."}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Token #</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.map((task) => (
                <TableRow key={task.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {task.tokenNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.category}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{task.location}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.priority}
                      color={getPriorityColor(task.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={task.status.replace("_", " ").toUpperCase()}
                      color={getStatusColor(task.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(task.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedTask(task);
                          setViewDialog(true);
                        }}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>

                      {task.status === "assigned" && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => openActionDialog(task, "accept")}
                            color="success"
                          >
                            <AcceptIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openActionDialog(task, "reject")}
                            color="error"
                          >
                            <RejectIcon />
                          </IconButton>
                        </>
                      )}

                      {task.status === "in_progress" && (
                        <IconButton
                          size="small"
                          onClick={() => openActionDialog(task, "resolve")}
                          color="success"
                        >
                          <ResolveIcon />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Task Dialog */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Task Details - {selectedTask?.tokenNumber}</DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Category
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTask.category}
                </Typography>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Priority
                </Typography>
                <Chip
                  label={selectedTask.priority}
                  color={getPriorityColor(selectedTask.priority)}
                  size="small"
                />

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Location
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTask.location}
                </Typography>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Status
                </Typography>
                <Chip
                  label={selectedTask.status.replace("_", " ").toUpperCase()}
                  color={getStatusColor(selectedTask.status)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Requester
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTask.requesterName}
                </Typography>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Contact Information
                </Typography>
                <Box display="flex" gap={1} mb={1}>
                  <PhoneIcon fontSize="small" />
                  <Typography variant="body2">
                    {selectedTask.contactPhone || "Not provided"}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <EmailIcon fontSize="small" />
                  <Typography variant="body2">
                    {selectedTask.requesterEmail}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Requested Date
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {new Date(selectedTask.createdAt).toLocaleString()}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTask.description}
                </Typography>
              </Grid>

              {selectedTask.workerNotes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Worker Notes
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedTask.workerNotes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog (Accept/Reject/Resolve) */}
      <Dialog
        open={actionDialog}
        onClose={() => setActionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionType === "accept"
            ? "Accept Task"
            : actionType === "reject"
            ? "Reject Task"
            : "Resolve Task"}{" "}
          - {selectedTask?.tokenNumber}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={
              actionType === "accept"
                ? "Notes (Optional)"
                : actionType === "reject"
                ? "Rejection Reason"
                : "Resolution Details"
            }
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            placeholder={
              actionType === "accept"
                ? "Add any notes about how you plan to handle this task..."
                : actionType === "reject"
                ? "Please provide a reason for rejecting this task..."
                : "Describe how the issue was resolved..."
            }
            sx={{ mt: 2 }}
            required={actionType !== "accept"}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setActionDialog(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAction}
            variant="contained"
            disabled={
              actionLoading || (actionType !== "accept" && !actionNotes.trim())
            }
            color={
              actionType === "accept"
                ? "success"
                : actionType === "reject"
                ? "error"
                : "primary"
            }
          >
            {actionLoading ? (
              <CircularProgress size={20} />
            ) : actionType === "accept" ? (
              "Accept Task"
            ) : actionType === "reject" ? (
              "Reject Task"
            ) : (
              "Mark as Resolved"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignedTasks;
